import { z } from "zod";

type TelemetryErrorInput =
  | Error
  | string
  | number
  | boolean
  | bigint
  | symbol
  | null
  | undefined;

const telemetryNumberSchema = z.number();
const telemetryBooleanSchema = z.boolean();
const telemetryStringSchema = z.string();

type TelemetryPrimitiveResult =
  | { readonly kind: "null" }
  | { readonly kind: "undefined" }
  | { readonly kind: "number"; readonly value: number }
  | { readonly kind: "boolean"; readonly value: boolean }
  | { readonly kind: "text"; readonly value: string }
  | { readonly kind: "not-primitive" };

export function parseTelemetryPrimitive<T>(value: T): TelemetryPrimitiveResult {
  if (Object.is(value, Number.NaN)) {
    return { kind: "text", value: "NaN" };
  }
  if (value === Number.POSITIVE_INFINITY) {
    return { kind: "text", value: "Infinity" };
  }
  if (value === Number.NEGATIVE_INFINITY) {
    return { kind: "text", value: "-Infinity" };
  }

  let primitiveTag: string;
  try {
    primitiveTag = Object.prototype.toString.call(value);
  } catch {
    return { kind: "not-primitive" };
  }

  if (primitiveTag === "[object Null]") {
    return { kind: "null" };
  }
  if (primitiveTag === "[object Undefined]") {
    return { kind: "undefined" };
  }
  if (primitiveTag === "[object Number]") {
    const numberResult = telemetryNumberSchema.safeParse(value);
    return numberResult.success
      ? { kind: "number", value: numberResult.data }
      : { kind: "not-primitive" };
  }
  if (primitiveTag === "[object Boolean]") {
    const booleanResult = telemetryBooleanSchema.safeParse(value);
    return booleanResult.success
      ? { kind: "boolean", value: booleanResult.data }
      : { kind: "not-primitive" };
  }
  if (primitiveTag === "[object String]") {
    const stringResult = telemetryStringSchema.safeParse(value);
    return stringResult.success
      ? { kind: "text", value: stringResult.data }
      : { kind: "not-primitive" };
  }
  if (primitiveTag === "[object BigInt]" || primitiveTag === "[object Symbol]") {
    return { kind: "text", value: String(value) };
  }
  return { kind: "not-primitive" };
}

export const CLIENT_EVENT_MAX_BYTES = 1024 * 1024;

const MESSAGE_MAX_LENGTH = 500;
const BODY_SNIPPET_MAX_LENGTH = 1000;
const STACK_TOP_MAX_LENGTH = 500;
const STRING_FIELD_MAX_LENGTH = 250;
const ROUTE_MAX_LENGTH = 300;
const FIELD_LIST_MAX_ITEMS = 200;

const sensitivePattern =
  /\b(authorization|cookie|access_token|refresh_token|id_token|token|password|secret|client_secret)\b\s*[:=]\s*("[^"]*"|'[^']*'|[^,\n\r;}]*)/gi;
const longOpaqueValuePattern = /\b[A-Za-z0-9+/_=-]{120,}\b/g;

const bpmActionSchema = z.enum([
  "send",
  "save",
  "claim",
  "unclaim",
  "reassign",
  "delete",
  "cancel",
]);

const clientEventTypeSchema = z.enum([
  "bpm.action.started",
  "bpm.action.succeeded",
  "bpm.action.failed",
  "bpm.validation.failed",
  "client.unhandled_error",
  "client.unhandled_rejection",
  "ui.error_boundary",
  "ui.request_error",
]);

const safeStringSchema = z.string().max(STRING_FIELD_MAX_LENGTH);
const safeRouteSchema = z.string().max(ROUTE_MAX_LENGTH);
const safeFieldNamesSchema = z.array(safeStringSchema).max(FIELD_LIST_MAX_ITEMS);

export const clientEventSchema = z.strictObject({
  eventId: z.string().regex(/^BPM-\d{8}-[a-f0-9]{8}$/),
  eventType: clientEventTypeSchema,
  timestamp: z.iso.datetime(),
  action: bpmActionSchema.optional(),
  route: safeRouteSchema,
  clientBuildSha: safeStringSchema,
  serverBuildSha: safeStringSchema.optional(),
  staleBundle: z.boolean().optional(),
  userEmail: safeStringSchema.optional(),
  bpm: z.strictObject({
    taskId: safeStringSchema.optional(),
    processInstanceId: safeStringSchema.optional(),
    processDefinitionId: safeStringSchema.optional(),
    processDefinitionKey: safeStringSchema.optional(),
    processKey: safeStringSchema.optional(),
    taskDefinitionKey: safeStringSchema.optional(),
    formKey: safeStringSchema.optional(),
    assignee: safeStringSchema.optional(),
  }).optional(),
  durationMs: z.number().nonnegative().max(24 * 60 * 60 * 1000).optional(),
  http: z.strictObject({
    endpoint: safeRouteSchema.optional(),
    status: z.number().int().min(100).max(599).optional(),
    statusText: safeStringSchema.optional(),
  }).optional(),
  error: z.strictObject({
    name: safeStringSchema.optional(),
    message: z.string().max(MESSAGE_MAX_LENGTH).optional(),
    bodySnippet: z.string().max(BODY_SNIPPET_MAX_LENGTH).optional(),
    stackTop: z.string().max(STACK_TOP_MAX_LENGTH).optional(),
    digest: safeStringSchema.optional(),
  }).optional(),
  form: z.strictObject({
    fieldNames: safeFieldNamesSchema.optional(),
    fieldCount: z.number().int().nonnegative().max(10000).optional(),
    fileFieldNames: safeFieldNamesSchema.optional(),
    fileCount: z.number().int().nonnegative().max(10000).optional(),
    approximateFileBytes: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).optional(),
    validationErrorFields: safeFieldNamesSchema.optional(),
    formValues: z.unknown().optional(),
    registeredFields: z.array(z.string()).optional(),
    dirtyFields: z.unknown().optional(),
    touchedFields: z.unknown().optional(),
    errors: z.unknown().optional(),
  }).optional(),
});

const errorBodySchema = z.looseObject({ body: z.unknown() });

export type BpmAction = z.infer<typeof bpmActionSchema>;
export type ClientEventType = z.infer<typeof clientEventTypeSchema>;
export type ClientEvent = z.infer<typeof clientEventSchema>;

function coerceText<T>(value: T): string | undefined {
  if (value instanceof Error) {
    return value.message;
  }

  const primitive = parseTelemetryPrimitive(value);
  if (primitive.kind === "null" || primitive.kind === "undefined") {
    return undefined;
  }
  if (primitive.kind === "number" || primitive.kind === "boolean" || primitive.kind === "text") {
    return String(primitive.value);
  }

  return Object.prototype.toString.call(value);
}

function sanitizeText<T>(value: T, maxLength: number): string | undefined {
  const text = coerceText(value);
  if (!text) {
    return undefined;
  }

  const redacted = text
    .replace(sensitivePattern, "$1=[redacted]")
    .replace(longOpaqueValuePattern, "[redacted-long-value]");

  return redacted.length > maxLength
    ? `${redacted.slice(0, Math.max(0, maxLength - 3))}...`
    : redacted;
}

export function truncateMessage<T>(value: T): string | undefined {
  return sanitizeText(value, MESSAGE_MAX_LENGTH);
}

function truncateBodySnippet<T>(value: T): string | undefined {
  return sanitizeText(value, BODY_SNIPPET_MAX_LENGTH);
}

function truncateStackTop<T>(value: T): string | undefined {
  const stack = coerceText(value);
  if (!stack) {
    return undefined;
  }

  const lines = stack.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const firstFrame = lines.find((line) => line.startsWith("at ") || line.includes("@")) ?? lines[0];
  return sanitizeText(firstFrame, STACK_TOP_MAX_LENGTH);
}

export function getSerializedSizeBytes<T>(value: T): number {
  let serialized = "";
  try {
    serialized = JSON.stringify(value) ?? "";
  } catch {
    return Number.POSITIVE_INFINITY;
  }

  const textEncoderConstructor = globalThis.TextEncoder;
  if (textEncoderConstructor) {
    return new textEncoderConstructor().encode(serialized).byteLength;
  }

  return serialized.length;
}

function normalizeTelemetryError<T>(error: T): TelemetryErrorInput {
  if (error instanceof Error) {
    return error;
  }

  const primitive = parseTelemetryPrimitive(error);
  if (primitive.kind === "null") {
    return null;
  }
  if (primitive.kind === "undefined") {
    return undefined;
  }
  if (primitive.kind === "number" || primitive.kind === "boolean") {
    return primitive.value;
  }
  if (primitive.kind === "text") {
    return primitive.value;
  }

  return coerceText(error) ?? "Unknown telemetry error";
}

export function sanitizeError<T>(error: T): ClientEvent["error"] {
  const normalizedError = normalizeTelemetryError(error);

  if (normalizedError instanceof Error) {
    const parsedErrorBody = errorBodySchema.safeParse(normalizedError);
    const bodySnippet = parsedErrorBody.success
      ? truncateBodySnippet(parsedErrorBody.data.body)
      : undefined;

    return {
      name: truncateMessage(normalizedError.name),
      message: truncateMessage(normalizedError.message),
      bodySnippet,
      stackTop: truncateStackTop(normalizedError.stack),
    };
  }

  return {
    message: truncateMessage(normalizedError),
  };
}

export function safeFieldNames<T>(fields: Iterable<T>): string[] {
  return Array.from(fields)
    .map((field) => sanitizeText(field, STRING_FIELD_MAX_LENGTH))
    .filter((field): field is string => Boolean(field))
    .slice(0, FIELD_LIST_MAX_ITEMS);
}
